import { useQuery } from '@tanstack/react-query';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import type { EmissionFactorTableIndexRowWire } from '@releve/shared';
import {
  emissionFactorReadBase,
  type EmissionFactorReadScope,
} from '@/lib/emissionFactorCacheKeys';

/**
 * One row of the slim emission-factor-TABLES index (the
 * `emission_factor_table_index` DB view). An ALIAS of the shared wire type —
 * re-exported here so every consumer (the settings grid, the detail host's
 * code resolution, the carbon picker, the account carbon settings panel and
 * the places emission-override fields) reaches the row contract through this
 * one hook.
 */
export type EmissionFactorTableIndexRow = EmissionFactorTableIndexRowWire;

/**
 * The COMPLETE emission-factor-table index for an org's scope: every GLOBAL
 * table (`organization_id IS NULL`) plus this org's own, with the per-table
 * ROLLUPS the old grid could not show — `factor_count`,
 * `active_factor_count`, `factors_missing_input_unit`, `column_count` and
 * `latest_factor_created_at`, all computed in SQL under the caller's RLS.
 *
 * BOUNDED TIER, not keyset: an org sees on the order of ten tables, so the
 * endpoint answers with one `.limit(1000)` select rather than a cursor loop.
 * The hook still goes through `indexQueryOptions` so the key shape, the
 * `{ items }` unwrap, the no-org gate and the cross-org placeholder guard are
 * the shared ones.
 *
 * `is_active` is NOT filtered here. The view normalises it
 * (`COALESCE(is_active, true)` — the canonical table reader's rule) and every
 * consumer filters for itself: the grid hides inactive rows behind a toggle,
 * while the pickers drop them outright. A hook-level filter would make the
 * grid's "show inactive" toggle impossible.
 *
 * ── Liveness contract ───────────────────────────────────────────────────────
 * The key is `['emission-factor-tables', orgId, 'index', {}]`.
 *
 * THE ENTITY PREFIX IS `'emission-factor-tables'` AND MUST STAY THAT WAY. It
 * is the prefix `invalidateEmissionFactorCaches`
 * (apps/web/src/lib/emissionFactorCacheKeys.ts) invalidates, which is the only
 * reason this index refreshes after a write — including after a FACTOR write,
 * because the rollup counts above live on these rows. Renaming the prefix
 * would produce no type error and no other failing test, just a list whose
 * counts silently stop moving. The literal-key assertion in
 * `useEmissionFactorTableIndex.test.ts` is the only guard; keep it.
 *
 * `staleTime` (60s, from indexQueryOptions) only suppresses redundant
 * mount/focus refetches between mutations.
 *
 * `scope` is an org id, or `EMISSION_FACTOR_SYSTEM_SCOPE` for the system
 * (global) tables only — `['emission-factor-tables', 'system', 'index', {}]`
 * via the org-less super-admin endpoint (System Settings).
 */
export const useEmissionFactorTableIndex = (scope?: EmissionFactorReadScope) =>
  // No params: the endpoint takes none, and the builder defaults key slot [3]
  // to {}. Passing an empty-ish object "for future filters" would fragment the
  // cache — don't.
  useQuery(
    indexQueryOptions<EmissionFactorTableIndexRowWire>({
      entity: 'emission-factor-tables',
      organizationId: scope,
      path: `${emissionFactorReadBase(scope)}/factor-tables/index`,
    }),
  );
